import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import type { IncomeSourceInput } from '@tahi/core';
import {
  addIncomeSource,
  deleteIncomeSource,
  restoreIncomeSource,
  editIncomeSource,
} from '../../../data/commands/budget';
import { useCommandEnv } from '../../../data/hooks';
import { Button, ScreenHeader, showToast } from '../../../ui';
import { useGoBack } from '../../spending/hooks';
import { IncomeSourceForm } from '../components/IncomeSourceForm';
import { copy, errorText } from '../copy';
import { useBudgetToday, useIncomeSource, useToday } from '../hooks';
import { SCREEN } from '../styles';

const BACK = { to: '/m/budget/income', label: copy.income };

export function IncomeSourceRoute() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const env = useCommandEnv();
  const navigate = useNavigate();
  const goBack = useGoBack('/m/budget/income');
  const today = useToday();
  const source = useIncomeSource(id ?? '');
  const s = useBudgetToday();
  const [error, setError] = useState<string | null>(null);
  const [armed, setArmed] = useState(false);

  if (id && source === undefined) return null;
  if (id && (!source || source.deletedAt)) {
    return (
      <main className={SCREEN}>
        <ScreenHeader title={copy.editSource} back={BACK} />
        <p className="text-muted">{errorText('not_found')}</p>
      </main>
    );
  }
  const editing = id ? source : null;
  const kind: 'salary' | 'bonus' = editing
    ? editing.kind === 'bonus'
      ? 'bonus'
      : 'salary'
    : sp.get('kind') === 'bonus'
      ? 'bonus'
      : 'salary';
  const initial: IncomeSourceInput | undefined = editing
    ? {
        name: editing.name,
        kind,
        schedule: editing.schedule,
        defaultAmounts: editing.defaultAmounts,
        weekendRule: editing.weekendRule,
        startDate: editing.startDate,
        endDate: editing.endDate ?? null,
        categoryId: editing.categoryId,
        accountId: editing.accountId ?? null,
      }
    : undefined;

  async function onSubmit(input: IncomeSourceInput) {
    const r = editing
      ? await editIncomeSource(env, editing.id, input)
      : await addIncomeSource(env, input);
    if (!r.ok) {
      setError(errorText(r.error));
      return;
    }
    goBack();
  }

  async function onDelete() {
    if (!editing) return;
    if (!armed) {
      setArmed(true);
      return;
    }
    await deleteIncomeSource(env, editing.id);
    const sourceId = editing.id;
    showToast({
      message: copy.deletedSource(editing.name),
      action: { label: copy.undo, onPress: () => void restoreIncomeSource(env, sourceId) },
    });
    navigate('/m/budget/income', { replace: true });
  }

  return (
    <main className={SCREEN}>
      <ScreenHeader
        title={editing ? copy.editSource : kind === 'bonus' ? copy.newBonus : copy.newSalary}
        back={BACK}
      />
      <IncomeSourceForm
        kind={kind}
        today={today}
        initial={initial}
        error={error}
        onSubmit={onSubmit}
        accounts={s?.accounts ?? []}
        salaries={(s?.sources ?? []).filter(
          (x) => x.kind === 'salary' && !x.deletedAt && x.id !== editing?.id,
        )}
      />
      {editing ? (
        <Button variant="destructive" block onClick={() => void onDelete()}>
          {armed ? copy.confirmDelete : copy.delete}
        </Button>
      ) : null}
    </main>
  );
}
